/**
 * Converts a Firestore Timestamp to a human-readable IST date string.
 * Format: "YYYY-MM-DD HH:mm" in UTC+5:30
 */
export const toISTString = (firestoreTimestamp) => {
  if (!firestoreTimestamp) return '—';
  try {
    const date = firestoreTimestamp.toDate();
    const istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
    return formatDate(istDate);
  } catch {
    return '—';
  }
};

/**
 * Converts a Firestore Timestamp to a local date string.
 * Format: "YYYY-MM-DD HH:mm"
 */
export const toLocalString = (firestoreTimestamp) => {
  if (!firestoreTimestamp) return '—';
  try {
    return formatDate(firestoreTimestamp.toDate());
  } catch {
    return '—';
  }
};

/**
 * Converts a Firestore Timestamp, Date, or date string to the "datetime-local" input format.
 * Format: "YYYY-MM-DDTHH:mm"  (needed for <input type="datetime-local">)
 */
export const toDatetimeLocal = (val) => {
  if (!val) return '';
  try {
    let date;
    if (val.toDate && typeof val.toDate === 'function') {
      date = val.toDate();
    } else if (val instanceof Date) {
      date = val;
    } else {
      date = new Date(val);
    }
    if (isNaN(date.getTime())) return '';
    return formatDate(date, 'T');
  } catch {
    return '';
  }
};

/**
 * Returns the next upcoming 15-minute interval from now (or a given date),
 * formatted as "YYYY-MM-DDTHH:mm".
 */
export const getNext15MinInterval = (baseDate = new Date()) => {
  const d = new Date(baseDate);
  d.setSeconds(0, 0);
  const minutes = d.getMinutes();
  const remainder = minutes % 15;
  const addMins = remainder === 0 ? 15 : 15 - remainder;
  d.setMinutes(minutes + addMins);
  return toDatetimeLocal(d);
};

/**
 * Returns a datetime string set to 6:00 AM on the target date (or tomorrow if 6:00 AM passed today).
 * Formatted as "YYYY-MM-DDTHH:mm".
 */
export const getDefault6AMDateTime = (baseDate = new Date()) => {
  const d = new Date(baseDate);
  d.setSeconds(0, 0);
  d.setHours(6, 0, 0, 0);
  if (d.getTime() < Date.now()) {
    d.setDate(d.getDate() + 1);
  }
  return toDatetimeLocal(d);
};

/**
 * Returns a datetime string set to 6:00 AM on the day after the start date (or tomorrow).
 * Formatted as "YYYY-MM-DDTHH:mm".
 */
export const getDefault6AMEndDateTime = (startVal) => {
  if (!startVal) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return getDefault6AMDateTime(d);
  }
  const s = startVal instanceof Date ? startVal : (startVal.toDate ? startVal.toDate() : new Date(startVal));
  if (isNaN(s.getTime())) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return getDefault6AMDateTime(d);
  }
  const end = new Date(s.getTime());
  end.setDate(end.getDate() + 1);
  end.setHours(6, 0, 0, 0);
  return toDatetimeLocal(end);
};

/**
 * Sets the time of a given date / datetime string to 06:00 AM.
 * Formatted as "YYYY-MM-DDTHH:mm".
 */
export const setDateTo6AM = (val) => {
  if (!val) return getDefault6AMDateTime();
  const d = val instanceof Date ? new Date(val) : (val.toDate ? val.toDate() : new Date(val));
  if (isNaN(d.getTime())) return getDefault6AMDateTime();
  d.setHours(6, 0, 0, 0);
  return toDatetimeLocal(d);
};

/**
 * Checks if a given date or datetime string has minutes aligned to a 15-minute boundary (:00, :15, :30, :45).
 */
export const isValid15MinInterval = (val) => {
  if (!val) return false;
  const d = val instanceof Date ? val : (val.toDate ? val.toDate() : new Date(val));
  if (isNaN(d.getTime())) return false;
  return d.getMinutes() % 15 === 0;
};

/**
 * Checks if end time is at least 15 minutes after start time.
 */
export const isAtLeast15MinDifference = (startVal, endVal) => {
  if (!startVal || !endVal) return false;
  const s = startVal instanceof Date ? startVal : (startVal.toDate ? startVal.toDate() : new Date(startVal));
  const e = endVal instanceof Date ? endVal : (endVal.toDate ? endVal.toDate() : new Date(endVal));
  if (isNaN(s.getTime()) || isNaN(e.getTime())) return false;
  return (e.getTime() - s.getTime()) >= 15 * 60 * 1000;
};

/**
 * Given a start time (string, Date, or Timestamp), returns a "YYYY-MM-DDTHH:mm" string
 * exactly 15 minutes after the start time.
 */
export const getMinEndTime = (startVal) => {
  if (!startVal) return '';
  const s = startVal instanceof Date ? startVal : (startVal.toDate ? startVal.toDate() : new Date(startVal));
  if (isNaN(s.getTime())) return '';
  const e = new Date(s.getTime() + 15 * 60 * 1000);
  return toDatetimeLocal(e);
};

/**
 * Returns the most recent completed Sunday at 23:59:59.
 * If today is Sunday, returns the previous week's Sunday.
 */
export const getLastSundayCutoff = (fromRefDate = new Date()) => {
  const d = new Date(fromRefDate);
  const dayOfWeek = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const daysSinceLastSunday = dayOfWeek === 0 ? 7 : dayOfWeek;
  const lastSunday = new Date(d);
  lastSunday.setDate(d.getDate() - daysSinceLastSunday);
  lastSunday.setHours(23, 59, 59, 999);
  return lastSunday;
};

/**
 * Checks if current day is Monday or later (Mon=1 through Sat=6).
 * On Sundays (0), current week is still running.
 */
export const isMondayOrLater = (fromRefDate = new Date()) => {
  const d = new Date(fromRefDate);
  return d.getDay() !== 0;
};

// ─── Private helpers ──────────────────────────────────────────────────────────
const pad = (n) => String(n).padStart(2, '0');

const formatDate = (date, sep = ' ') => {
  const y  = date.getFullYear();
  const mo = pad(date.getMonth() + 1);
  const d  = pad(date.getDate());
  const h  = pad(date.getHours());
  const mi = pad(date.getMinutes());
  return `${y}-${mo}-${d}${sep}${h}:${mi}`;
};
